import React from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  HStack,
  Badge,
  Divider,
  Icon,
  SimpleGrid,
} from '@chakra-ui/react';
import { FiShield, FiLock, FiEye, FiServer, FiMail, FiPhone, FiMapPin } from 'react-icons/fi';
import { Link } from 'react-router-dom';

function PrivacyPolicy() {
  return (
    <Box bg="#F8FAFC" minH="100vh" py={{ base: 8, md: 12 }}>
      {/* Header Banner */}
      <Box
        bg="linear-gradient(135deg, #0B132B 0%, #162237 60%, #1E293B 100%)"
        color="white"
        py={{ base: 12, md: 16 }}
        mb={{ base: 8, md: 12 }}
      >
        <Container maxW="5xl" px={{ base: 4, md: 8 }} textAlign="center">
          <Badge
            bg="rgba(234, 179, 8, 0.15)"
            color="#FACC15"
            border="1px solid"
            borderColor="rgba(234, 179, 8, 0.3)"
            px={3.5}
            py={1}
            borderRadius="full"
            fontSize="xs"
            fontWeight="semibold"
            letterSpacing="wider"
            textTransform="uppercase"
            mb={3}
          >
            Statutory Compliance & Privacy
          </Badge>
          <Heading as="h1" size={{ base: 'xl', md: '2xl' }} fontWeight="extrabold">
            Privacy Policy
          </Heading>
          <Text fontSize="sm" color="gray.300" mt={2}>
            Last Updated: January 2025 | Applicable to all digital services, software suites, and bookstore orders
          </Text>
        </Container>
      </Box>

      {/* Main Document Content */}
      <Container maxW="5xl" px={{ base: 4, md: 8 }}>
        <Box
          bg="white"
          p={{ base: 6, md: 12 }}
          borderRadius="2xl"
          border="1px solid"
          borderColor="gray.100"
          boxShadow="sm"
        >
          {/* Quick Highlight Cards */}
          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4} mb={10}>
            <Box p={5} bg="#F8FAFC" borderRadius="xl" border="1px solid" borderColor="gray.100">
              <Icon as={FiShield} boxSize={6} color="#162237" mb={2} />
              <Text fontWeight="bold" fontSize="sm" color="#162237">
                No Data Reselling
              </Text>
              <Text fontSize="xs" color="gray.500" mt={1}>
                We never participate in selling, renting, or trading advocate and customer data lists.
              </Text>
            </Box>
            <Box p={5} bg="#F8FAFC" borderRadius="xl" border="1px solid" borderColor="gray.100">
              <Icon as={FiLock} boxSize={6} color="#162237" mb={2} />
              <Text fontWeight="bold" fontSize="sm" color="#162237">
                Encrypted Processing
              </Text>
              <Text fontSize="xs" color="gray.500" mt={1}>
                Payments and account credentials are guarded via 256-bit SSL encryption.
              </Text>
            </Box>
            <Box p={5} bg="#F8FAFC" borderRadius="xl" border="1px solid" borderColor="gray.100">
              <Icon as={FiEye} boxSize={6} color="#162237" mb={2} />
              <Text fontWeight="bold" fontSize="sm" color="#162237">
                Judicial Confidentiality
              </Text>
              <Text fontSize="xs" color="gray.500" mt={1}>
                Case search histories and research citations are private to your logged session.
              </Text>
            </Box>
          </SimpleGrid>

          <VStack spacing={8} align="stretch" color="gray.700" fontSize="sm" lineHeight="tall">
            <Box>
              <Heading as="h2" size="md" color="#162237" mb={3} display="flex" alignItems="center" gap={2}>
                <Box w="3px" h="18px" bg="#EAB308" borderRadius="full" />
                1. Commitment to Personal Data Protection
              </Heading>
              <Text>
                At <strong>Navneet Law Studio Pvt. Ltd.</strong>, we recognize the supreme importance of protecting the personal information and confidentiality of legal practitioners, law firms, judicial officers, and academic institutions who patronize our platform. We strictly do not give out, rent, or participate in the commercial re-selling of customer records, contact directories, or subscription indices.
              </Text>
            </Box>

            <Divider borderColor="gray.100" />

            <Box>
              <Heading as="h2" size="md" color="#162237" mb={3} display="flex" alignItems="center" gap={2}>
                <Box w="3px" h="18px" bg="#EAB308" borderRadius="full" />
                2. Information Collected During Registration & Software Activation
              </Heading>
              <Text mb={3}>
                When you register on <strong>www.navneetlawstudio.com</strong> or activate judicial research modules (including Supreme Today AI, CDJ Law Journal, LegitQuest, DLT, and Digilegal), we require specific personal data such as your full legal name, bar registration number (optional), email address, mobile contact, and chamber address for account synchronization and license authentication.
              </Text>
              <Text>
                We do not furnish this information to external marketing brokers. We reserve the right to contact you exclusively for license renewals, order dispatch updates, or direct clarification regarding your legal software or literature requirements.
              </Text>
            </Box>

            <Divider borderColor="gray.100" />

            <Box>
              <Heading as="h2" size="md" color="#162237" mb={3} display="flex" alignItems="center" gap={2}>
                <Box w="3px" h="18px" bg="#EAB308" borderRadius="full" />
                3. Order Fulfillment & Courier Transmission
              </Heading>
              <Text>
                When ordering physical law volumes, loose-leaf journals, or bespoke advocate attire, your shipping address and phone number are shared solely with authorized logistical carriers (such as BlueDart, Delhivery, or Indian Registered Post) for the singular objective of physical delivery.
              </Text>
            </Box>

            <Divider borderColor="gray.100" />

            <Box>
              <Heading as="h2" size="md" color="#162237" mb={3} display="flex" alignItems="center" gap={2}>
                <Box w="3px" h="18px" bg="#EAB308" borderRadius="full" />
                4. Policy Modifications & Periodic Revisions
              </Heading>
              <Text>
                Navneet Law Studio reserves the right to modify or amend this Privacy Policy to comply with statutory legal frameworks and the Digital Personal Data Protection (DPDP) Act. All updates take immediate effect upon publishing to this portal.
              </Text>
            </Box>

            <Divider borderColor="gray.100" />

            {/* Official Contact Box */}
            <Box bg="#F8FAFC" p={{ base: 6, md: 8 }} borderRadius="xl" border="1px solid" borderColor="gray.200">
              <Heading as="h3" size="sm" color="#162237" mb={4}>
                Privacy Officer & Data Grievance Redressal
              </Heading>
              <Text fontSize="sm" color="gray.600" mb={4}>
                For any clarifications or data modification requests, you may formally contact our registered headquarters:
              </Text>

              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                <HStack align="flex-start" spacing={3}>
                  <Icon as={FiMapPin} boxSize={5} color="#162237" mt={1} />
                  <Box fontSize="xs">
                    <Text fontWeight="bold" color="#162237">
                      Navneet Law Studio Pvt. Ltd.
                    </Text>
                    <Text color="gray.500">
                      Bldg 4B-605, SDPL Ashray, Beltarodi, Nagpur, MH – 440037
                    </Text>
                  </Box>
                </HStack>

                <VStack align="flex-start" spacing={2} fontSize="xs">
                  <HStack spacing={2}>
                    <Icon as={FiPhone} color="#162237" />
                    <Text color="gray.600">
                      <strong>Phone:</strong> +91 84213 85239 / +91 98111 23456
                    </Text>
                  </HStack>
                  <HStack spacing={2}>
                    <Icon as={FiMail} color="#162237" />
                    <Text color="gray.600">
                      <strong>Email:</strong> customercare@navneetlawstudio.com
                    </Text>
                  </HStack>
                </VStack>
              </SimpleGrid>
            </Box>
          </VStack>
        </Box>
      </Container>
    </Box>
  );
}

export default PrivacyPolicy;
